import { z } from "zod";
import { renderConfigSchema as renderConfigV3Schema } from "./render-config.js";
import { renderConfigV2Schema } from "./render.js";
import { tierSchema } from "./subscription.js";
import { Platform } from "./platform.js";
export const ShareGroupVideoAudiences = [
    "recipient",
    "all",
    "submitters",
];
export var CompilationStatus;
(function (CompilationStatus) {
    CompilationStatus[CompilationStatus["CREATED"] = 0] = "CREATED";
    CompilationStatus[CompilationStatus["RENDERING"] = 1] = "RENDERING";
    CompilationStatus[CompilationStatus["FAILED"] = 2] = "FAILED";
    CompilationStatus[CompilationStatus["COMPLETE"] = 3] = "COMPLETE";
    CompilationStatus[CompilationStatus["ARCHIVED"] = 4] = "ARCHIVED";
    // v3 statuses
    CompilationStatus[CompilationStatus["PREPARING"] = 5] = "PREPARING";
    CompilationStatus[CompilationStatus["STITCHING"] = 6] = "STITCHING";
})(CompilationStatus || (CompilationStatus = {}));
export const tierInfoSchema = z.object({
    userTier: tierSchema,
    ownerTier: tierSchema,
    collaboratorTier: tierSchema,
    cardTier: tierSchema,
});
const chunkStatuses = ["pending", "complete"];
export const chunkSchema = z.object({
    chunkId: z.string().min(1),
    chunkNumber: z.number().positive(),
    chunkCount: z.number().positive(),
    failures: z.number().nonnegative(),
    chunkS3Key: z.string(),
    status: z.enum(chunkStatuses),
});
const taskActivityStampSchema = z.object({
    // The first timestamp (ms) of this activity. This will match lastActivity on
    // the first activity ping.
    firstActivity: z.number(),
    // The last time (ms) we heard from this task.
    lastActivity: z.number(),
});
const renderStatusDetailsSchema = z.object({
    // When the render was requested, i.e. when the compilation moved into
    // PREPARING. It covers the whole pipeline, not just the render routine, so
    // elapsed time can be measured before any ECS task has reported in.
    startedAt: z.number().optional(),
    completedAt: z.number().optional(),
    // Information about the last save request. Let's us e.g. throw away old
    // requests if they arrive out of order.
    lastSaveRequest: z
        .object({
        sequenceNumber: z.number(),
        // milliseconds timestamp
        sentAt: z.number(),
    })
        .optional(),
    // Records activity from the render task. This is optional because renders
    // started before this feature was added won't have this information.
    activity: z
        .object({
        prepare: taskActivityStampSchema.optional(),
        stitch: taskActivityStampSchema.optional(),
        // keys are chunkIds
        render: z.record(z.string(), taskActivityStampSchema).optional(),
    })
        .optional(),
    chunks: z.array(chunkSchema),
    // Tells us whether task hung checks are running for each task. This is mostly
    // here as an extra safeguard against running multiple task hung checks for a
    // single render task.
    hungChecks: z.object({
        prepare: z.boolean(),
        stitch: z.boolean(),
        render: z.object({
            // a list of ids for chunks that have task hung checks running
            chunkIds: z.array(z.string()),
        }),
    }),
    // storing these individually is better than a single `failures` number
    // because this way we don't need to remember to reset to `0` when moving
    // between steps
    failures: z.object({
        prepare: z.number(),
        stitch: z.number(),
    }),
});
const renderRequestV3Schema = z.object({
    renderConfig: renderConfigV3Schema,
    // optional because it won't be set until the render starts
    remotionBundleUrl: z.string().url().optional(),
    status: renderStatusDetailsSchema,
});
// the schema returned by the API and the schema stored in the database are
// slightly different. This is the schema stored in the database.
const compilationV1DatabaseSchema = z.object({
    // version 1 or undefined is the original renderer (no longer used)
    version: z.literal(1).optional(),
    compilationId: z.string(),
    cardId: z.string(),
    cardUrl: z.string(),
    titleText: z.string().optional(),
    thumbUrl: z.string().optional(),
    compilationStatus: z.enum(CompilationStatus),
    videoUrl: z.string().optional(),
    createdAt: z.number(),
    renderRequest: renderConfigV2Schema.optional(),
    segmentIds: z.array(z.string()),
    tierInfo: tierInfoSchema.optional(),
    platform: z.enum(Platform).optional(),
    retryCount: z.number().optional(),
    // Using `any` here because using cardSchema directly will create a circular
    // dependency (cardSchema -> compilationSchema -> cardSchema). The Compilation
    // type below is extended to include the correct Card type.
    card: z.any().optional(),
});
// the schema returned by the API and the schema stored in the database are
// slightly different. This is the schema returned by the API.
const compilationV1Schema = compilationV1DatabaseSchema.extend({
    videoCdnUrl: z.string(),
    thumbUrl: z.string(),
    videoUrl: z.string(),
});
// the schema returned by the API and the schema stored in the database are
// slightly different. This is the schema stored in the database.
export const compilationV2DatabaseSchema = z.object({
    // 2 is the Python-based renderer
    version: z.literal(2),
    compilationId: z.string(),
    cardId: z.string(),
    cardUrl: z.string(),
    titleText: z.string().optional(),
    thumbUrl: z.string().optional(),
    compilationStatus: z.enum(CompilationStatus),
    videoUrl: z.string().optional(),
    createdAt: z.number(),
    renderRequest: renderConfigV2Schema,
    segmentIds: z.array(z.string()),
    tierInfo: tierInfoSchema.optional(),
    platform: z.enum(Platform).optional(),
    retryCount: z.number().optional(),
    // Using `any` here because using cardSchema directly will create a circular
    // dependency (cardSchema -> compilationSchema -> cardSchema). The Compilation
    // type below is extended to include the correct Card type.
    card: z.any().optional(),
});
// the schema returned by the API and the schema stored in the database are
// slightly different. This is the schema returned by the API.
export const compilationV2Schema = compilationV2DatabaseSchema.extend({
    videoCdnUrl: z.string(),
    thumbUrl: z.string(),
    videoUrl: z.string(),
});
// the schema returned by the API and the schema stored in the database are
// slightly different. This is the schema stored in the database.
export const compilationV3DatabaseSchema = z.object({
    // 3 is the Remotion-based renderer
    version: z.literal(3),
    compilationId: z.string(),
    cardId: z.string(),
    cardUrl: z.string(),
    compilationStatus: z.enum(CompilationStatus),
    videoUrl: z.string().optional(),
    thumbUrl: z.string().optional(),
    createdAt: z.number(),
    renderRequest: renderRequestV3Schema,
    tierInfo: tierInfoSchema.optional(),
    platform: z.enum(Platform).optional(),
    retryCount: z.number().optional(),
    // Using `any` here because using cardSchema directly will create a circular
    // dependency (cardSchema -> compilationSchema -> cardSchema). The Compilation
    // types below are extended to include the correct Card type.
    card: z.any().optional(),
});
// the schema returned by the API and the schema stored in the database are
// slightly different. This is the schema returned by the API.
export const compilationV3Schema = compilationV3DatabaseSchema.extend({
    videoCdnUrl: z.string(),
    thumbUrl: z.string(),
    videoUrl: z.string(),
});
// can't use discriminatedUnion because the v1 version could be undefined
export const compilationSchema = z.union([
    // putting v3 first since that's the mostly likely to match
    compilationV3Schema,
    compilationV2Schema,
    compilationV1Schema,
]);
// can't use discriminatedUnion because the v1 version could be undefined
export const compilationDatabaseSchema = z.union([
    compilationV1DatabaseSchema,
    compilationV2DatabaseSchema,
    compilationV3DatabaseSchema,
]);
